import { Box, BoxProps } from '@mui/system'
import React from 'react'
import { ITooltip, Tooltip as ReactTooltip, TooltipRefProps } from 'react-tooltip'

import { MobileClickArea } from '@/atoms/MobileClickArea'
import { mainBlack } from '@/refactor/colors'
import styled, { css, FontFamily } from '@/styles'

export type TooltipPlace = ITooltip['place']

export type IProps<C extends React.ElementType = typeof Box> = React.PropsWithChildren<{
	place?: TooltipPlace
	title?: string | React.ReactNode
	tipClassName?: string
	render?: (title: string) => React.ReactNode
	component?: C
	disable?: boolean
	multiline?: boolean
	autoWidth?: boolean
	'data-test'?: string
}> &
	Omit<React.ComponentPropsWithoutRef<C>, 'component'>

const MOBILE_SCREEN_OFFSET_PX = 15

const StyledReactTooltip = styled(ReactTooltip)<{ fixedWidth?: boolean }>`
	font-family: ${FontFamily.Comfortaa};
	font-size: ${(p) => p.theme.typography.pxToRem(12)};
	font-weight: bold;
	line-height: 1.5;

	border-radius: 2px;
	padding: ${(p) => p.theme.spacing(2)} !important;
	background-color: ${mainBlack} !important;
	z-index: 99999999;

	${({ fixedWidth: $fixedWidth }) =>
		$fixedWidth
			? css`
					max-width: 350px;
					white-space: wrap;
				`
			: ''}

	${(p) => p.theme.breakpoints.down('sm')} {
		max-width: calc(100vw - ${MOBILE_SCREEN_OFFSET_PX * 2}px);
	}

	& .react-tooltip-arrow {
		background-color: ${mainBlack} !important;
	}
`

let tooltipId = 0

function getTooltipId() {
	return tooltipId++
}

function Tooltip<C extends React.ElementType = typeof Box>(
	props: IProps<C> & Omit<React.ComponentProps<C>, keyof IProps<C>>,
) {
	const {
		className,
		tipClassName,
		children,
		title,
		component: Component = Span,
		render,
		place,
		disable,
		multiline,
		autoWidth,
		...others
	} = props

	const [tooltipId] = React.useState(getTooltipId)
	const id = `tt-${tooltipId}`

	const tooltipRef = React.useRef<TooltipRefProps | null>(null)

	React.useEffect(() => {
		const handleHideAll = () => {
			tooltipRef.current?.close()
		}
		window.addEventListener('app-hide-all-tooltips', handleHideAll)
		return () => {
			window.removeEventListener('app-hide-all-tooltips', handleHideAll)
		}
	}, [])

	const content = render?.(String(title) ?? '') ?? title
	if (!content) {
		return (
			<Component className={className} {...others}>
				{children}
			</Component>
		)
	}

	return (
		<React.Fragment>
			<Component className={className} data-tooltip-id={disable ? undefined : id} {...others}>
				<MobileClickArea />
				{children}
			</Component>
			{!disable && (
				<StyledReactTooltip
					ref={tooltipRef}
					id={id}
					fixedWidth={!autoWidth}
					className={tipClassName}
					variant="dark"
					place={place}
					delayHide={50}
					delayShow={100}
					portalRoot={document.body}
					positionStrategy="fixed"
				>
					{content}
				</StyledReactTooltip>
			)}
		</React.Fragment>
	)
}

Tooltip.hide = () => {
	window.dispatchEvent(new CustomEvent('app-hide-all-tooltips'))
}

export default styled(Tooltip)`
	position: relative;
`

function Span(props: BoxProps) {
	return <Box {...props} component="span" />
}
